import React from 'react';
import { Menu, MenuItem, Tooltip, Divider, CircularProgress } from '@mui/material';
import { useUser } from './WebSocketContext';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

/**
 * CaseContextMenu Component
 * 
 * A context menu component that appears when right-clicking or clicking the menu button on a case item.
 * Provides quick access to case actions like rename and duplicate with helpful tooltips.
 * 
 * Features:
 * - Rename case functionality with tooltip
 * - Duplicate/copy case functionality with tooltip
 * - Consistent styling with purple theme
 * - Internationalization support
 * - Hover effects and icons
 * - Helpful tooltips explaining each action
 */
const CaseContextMenu = ({
  anchorEl,
  open,
  onClose,
  onRename,
  onCopy,
  onSaveAsTemplate,
  onAddToCollection,
  onDelete,
  isDeleting = false,
  primaryColor = '#5757b0',
  lightPurple = '#f0f0ff'
}) => {
  const { t } = useTranslation();
  const { isReadOnly } = useUser();

  const handleRename = () => {
    onRename();
    onClose();
  };

  const handleCopy = () => {
    onCopy();
    onClose();
  };

  const handleSaveAsTemplate = () => {
    onSaveAsTemplate();
    onClose();
  };

  const handleAddToCollection = () => {
    onAddToCollection();
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  return (
    <Menu
      id="case-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(87, 87, 176, 0.15)',
          mt: 1
        }
      }}
    >
      {!isReadOnly && (
      <Tooltip 
        title={t('sidebar.rename_case_tooltip') || 'Change the name of this case'}
        placement="left"
        arrow
      >
        <MenuItem onClick={handleRename} sx={{ 
          minWidth: '150px',
          '&:hover': { backgroundColor: lightPurple }
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1, color: primaryColor }} />
          {t('sidebar.rename_case') || 'Rename'}
        </MenuItem>
      </Tooltip>
      )}
      
      {!isReadOnly && (
      <Tooltip 
        title={t('sidebar.copy_case_tooltip') || 'Create a copy of this case with all its data'}
        placement="left"
        arrow
      >
        <MenuItem onClick={handleCopy} sx={{ 
          minWidth: '150px',
          '&:hover': { backgroundColor: lightPurple }
        }}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1, color: primaryColor }} />
          {t('sidebar.copy_case') || 'Duplicate'}
        </MenuItem>
      </Tooltip>
      )}

      {!isReadOnly && (
      <Tooltip 
        title={t('template.save_as_template_tooltip') || 'Save this case as a reusable template'}
        placement="left"
        arrow
      >
        <MenuItem onClick={handleSaveAsTemplate} sx={{ 
          minWidth: '150px',
          '&:hover': { backgroundColor: lightPurple }
        }}>
          <SaveIcon fontSize="small" sx={{ mr: 1, color: primaryColor }} />
          {t('template.save_as_template') || 'Save as Template'}
        </MenuItem>
      </Tooltip>
      )}

      {!isReadOnly && (
      <Tooltip 
        title={t('collection.add_to_collection_tooltip') || 'Add this case to a collection'}
        placement="left"
        arrow
      >
        <MenuItem onClick={handleAddToCollection} sx={{ 
          minWidth: '150px',
          '&:hover': { backgroundColor: lightPurple }
        }}>
          <FolderIcon fontSize="small" sx={{ mr: 1, color: primaryColor }} />
          {t('collection.add_to_collection') || 'Add to Collection'}
        </MenuItem>
      </Tooltip>
      )}

      {!isReadOnly && (
      <>
        <Divider sx={{ my: 0.5 }} />
        <Tooltip 
          title={t('sidebar.delete_case_tooltip') || 'Delete this case permanently'}
          placement="left"
          arrow
        >
          <MenuItem onClick={handleDelete} disabled={isDeleting} sx={{ 
            minWidth: '150px',
            '&:hover': { backgroundColor: '#ffe6e6' },
            color: '#ff6666'
          }}>
            {isDeleting ? (<CircularProgress size={14} sx={{ color: '#ff6666', mr: 1 }} />) : (<DeleteIcon fontSize="small" sx={{ mr: 1, color: '#ff6666' }} />)}
            {isDeleting ? (t('sidebar.delete_in_progress') || 'Deleting...') : (t('sidebar.delete_case') || 'Delete')}
          </MenuItem>
        </Tooltip>
      </>
      )}
    </Menu>
  );
};

export default CaseContextMenu;
